import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
// KELSH Product Description Accordion
document.addEventListener('DOMContentLoaded', function() {
  setTimeout(function() {
    const description = document.querySelector('.product__description, .rte, [class*="description"]');
    if (!description) return;

    const paragraphs = Array.from(description.querySelectorAll('p, h3, h2'));
    const sections = [];
    let currentSection = null;

    paragraphs.forEach(function(node) {
      const text = node.textContent.trim();
      if (text === 'The Story' || text === 'The Details') {
        if (currentSection) sections.push(currentSection);
        currentSection = { title: text, nodes: [], originalNode: node };
      } else if (currentSection) {
        currentSection.nodes.push(node);
      }
    });

    if (currentSection) sections.push(currentSection);
    if (sections.length === 0) return;

    sections.forEach(function(section, index) {
      const header = document.createElement('div');
      header.className = 'kelsh-accordion-header';
      header.textContent = section.title;

      const body = document.createElement('div');
      body.className = 'kelsh-accordion-body';

      section.nodes.forEach(function(node) {
        body.appendChild(node.cloneNode(true));
        node.style.display = 'none';
      });

      header.addEventListener('click', function() {
        header.classList.toggle('open');
        body.classList.toggle('open');
      });

      section.originalNode.replaceWith(header);
      header.after(body);
    });
  }, 500);
});
